import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Funnel, Plus, X, FloppyDisk, ArrowsClockwise, Lightning, WarningCircle, ArrowUp, ArrowDown, Sparkle,
} from "@phosphor-icons/react";
import SourceSuggestionsPanel from "./SourceSuggestionsPanel";

const NEW_SOURCE = { name: "", aliases: [] };

export default function SourcesTab({ settings, onSaved }) {
  const [sources, setSources] = useState(() => (settings.sources || []).map((s) => ({
    name: s.name || "",
    aliases: Array.isArray(s.aliases) ? [...s.aliases] : [],
  })));
  const [unmapped, setUnmapped] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [newAliasIdx, setNewAliasIdx] = useState({}); // {idx: 'draft alias text'}

  const fetchUnmapped = async () => {
    try {
      const [{ data }, { data: sug }] = await Promise.all([
        api.get("/sources/unmapped"),
        api.get("/sources/suggestions"),
      ]);
      setUnmapped(data.unmapped || []);
      setSuggestions(sug.suggestions || []);
    } catch (e) {
      console.debug("sources fetch failed:", e?.response?.status || e?.message);
    }
  };

  useEffect(() => { fetchUnmapped(); }, []);

  // Server-side taxonomy changed (AI auto-apply / accepted suggestion) —
  // adopt it so a later manual Save doesn't overwrite the learned aliases.
  const adoptSources = (next) => {
    if (Array.isArray(next)) {
      setSources(next.map((s) => ({ name: s.name || "", aliases: Array.isArray(s.aliases) ? [...s.aliases] : [] })));
      onSaved?.();
    }
  };

  const runAi = async () => {
    setAiRunning(true);
    try {
      const { data } = await api.post("/sources/ai-suggest");
      const applied = data.auto_applied?.length || 0;
      toast.success(
        `AI mapped ${applied} source${applied === 1 ? "" : "s"} automatically`
        + (data.proposed ? `, proposed ${data.proposed} new source${data.proposed === 1 ? "" : "s"}` : "")
        + (data.suggested ? `, ${data.suggested} mapping${data.suggested === 1 ? "" : "s"} to review` : ""),
        { duration: 12000 },
      );
      adoptSources(data.sources);
      fetchUnmapped();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI mapping failed");
    } finally {
      setAiRunning(false);
    }
  };

  const onSuggestionResolved = (id, nextSources) => {
    setSuggestions((cur) => cur.filter((s) => s.id !== id));
    if (nextSources) {
      adoptSources(nextSources);
      fetchUnmapped();
    }
  };

  const onSuggestionsResolvedAll = (nextSources, skipped) => {
    const skippedIds = new Set((skipped || []).map((s) => s.id));
    setSuggestions((cur) => cur.filter((s) => skippedIds.has(s.id)));
    adoptSources(nextSources);
    fetchUnmapped();
  };

  const save = async () => {
    // strip empty-name rows; keep order
    const cleaned = sources
      .map((s) => ({
        name: (s.name || "").trim(),
        aliases: (s.aliases || []).map((a) => (a || "").trim()).filter(Boolean),
      }))
      .filter((s) => s.name);
    if (cleaned.length === 0) {
      toast.error("At least one canonical source is required.");
      return;
    }
    // detect duplicate names
    const names = cleaned.map((s) => s.name.toLowerCase());
    if (new Set(names).size !== names.length) {
      toast.error("Duplicate source names found — each canonical name must be unique.");
      return;
    }
    setSaving(true);
    try {
      await api.patch("/settings", { sources: cleaned });
      toast.success(`Saved ${cleaned.length} sources — existing leads re-canonicalized`);
      onSaved?.();
      fetchUnmapped();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const recompute = async () => {
    setRecomputing(true);
    try {
      const { data } = await api.post("/sources/recompute");
      toast.success(`Recanonicalized ${data.updated} lead(s)`);
      fetchUnmapped();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed");
    } finally {
      setRecomputing(false);
    }
  };

  const updateSource = (idx, patch) => {
    setSources((cur) => cur.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const removeSource = (idx) => {
    setSources((cur) => cur.filter((_, i) => i !== idx));
  };

  const addSource = () => {
    setSources((cur) => [...cur, { ...NEW_SOURCE, aliases: [] }]);
  };

  const moveSource = (idx, dir) => {
    setSources((cur) => {
      const target = idx + dir;
      if (target < 0 || target >= cur.length) return cur;
      const copy = [...cur];
      [copy[idx], copy[target]] = [copy[target], copy[idx]];
      return copy;
    });
  };

  const addAlias = (idx) => {
    const draft = (newAliasIdx[idx] || "").trim();
    if (!draft) return;
    setSources((cur) => cur.map((s, i) => (
      i === idx ? { ...s, aliases: [...(s.aliases || []), draft] } : s
    )));
    setNewAliasIdx((m) => ({ ...m, [idx]: "" }));
  };

  const removeAlias = (idx, aliasIdx) => {
    setSources((cur) => cur.map((s, i) => (
      i === idx ? { ...s, aliases: s.aliases.filter((_, j) => j !== aliasIdx) } : s
    )));
  };

  // Quickly map an unmapped raw source -> existing canonical source by
  // appending it as an alias on the chosen entry.
  const mapUnmappedToExisting = (raw, targetIdx) => {
    setSources((cur) => cur.map((s, i) => (
      i === targetIdx ? { ...s, aliases: [...(s.aliases || []), raw] } : s
    )));
    toast.message(`"${raw}" added as alias of "${sources[targetIdx].name}" — click Save to apply.`);
  };

  return (
    <div data-testid="sources-tab" className="space-y-8 max-w-4xl">
      <section className="bg-white border border-slate-200 rounded-sm p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-sm bg-emerald-50 flex items-center justify-center shrink-0">
            <Funnel size={24} className="text-emerald-600" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-2xl text-slate-900">Lead sources</h2>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
              Define the canonical buckets a lead should be classified into.
              The AI's raw output (e.g. <span className="font-mono text-slate-700">"Auto and General Insurance Company Limited"</span>)
              is matched against each row's <em>aliases</em> (case-insensitive substring; first match wins).
              Reorder rows to control precedence — keep the most specific entries near the top.
            </p>
          </div>
          <Button
            data-testid="sources-recompute-btn"
            variant="outline"
            size="sm"
            className="rounded-sm gap-2"
            onClick={recompute}
            disabled={recomputing}
          >
            <ArrowsClockwise size={12} className={recomputing ? "animate-spin" : ""} />
            {recomputing ? "Recomputing…" : "Recompute all"}
          </Button>
        </div>
      </section>

      {/* AI suggestions */}
      <SourceSuggestionsPanel suggestions={suggestions} onResolved={onSuggestionResolved} onResolvedAll={onSuggestionsResolvedAll} />

      {/* Unmapped queue */}
      {unmapped.length > 0 && (
        <section data-testid="sources-unmapped-panel" className="bg-amber-50 border border-amber-200 rounded-sm p-6 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <WarningCircle size={16} className="text-amber-700" weight="fill" />
            <div className="label-tracking !text-amber-900">{unmapped.length} unmapped source{unmapped.length === 1 ? "" : "s"}</div>
            <Button
              data-testid="sources-ai-map-btn"
              size="sm"
              disabled={aiRunning}
              onClick={runAi}
              className="ml-auto h-8 rounded-sm gap-1.5 bg-indigo-700 hover:bg-indigo-800"
            >
              <Sparkle size={12} weight="fill" className={aiRunning ? "animate-pulse" : ""} />
              {aiRunning ? "Classifying…" : "Auto-map with AI"}
            </Button>
          </div>
          <p className="text-xs text-amber-800">
            These free-text source values don't match any current alias. Let the AI classify them (confident matches apply instantly, the rest come back as suggestions), or map each manually to an existing canonical source.
          </p>
          <div className="space-y-2">
            {unmapped.map((u) => (
              <div key={u.raw} data-testid={`unmapped-${u.raw}`} className="flex items-center gap-3 flex-wrap bg-white border border-amber-200 rounded-sm p-3">
                <span className="font-mono text-xs text-slate-800 flex-1 break-all">{u.raw}</span>
                <span className="text-[0.65rem] text-slate-500">{u.count} lead{u.count === 1 ? "" : "s"}</span>
                <select
                  data-testid={`unmapped-map-${u.raw}`}
                  className="h-8 rounded-sm border border-slate-300 text-xs px-2"
                  defaultValue=""
                  onChange={(e) => {
                    const idx = parseInt(e.target.value, 10);
                    if (!isNaN(idx)) mapUnmappedToExisting(u.raw, idx);
                    e.target.value = "";
                  }}
                >
                  <option value="" disabled>Map to →</option>
                  {sources.map((s, i) => (
                    <option key={i} value={i}>{s.name || "(unnamed)"}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Sources list */}
      <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="label-tracking">Canonical sources ({sources.length})</div>
          <Button
            data-testid="sources-add-btn"
            size="sm"
            variant="outline"
            className="rounded-sm gap-2"
            onClick={addSource}
          >
            <Plus size={12} /> Add source
          </Button>
        </div>

        <div className="space-y-3">
          {sources.map((s, idx) => (
            <div key={idx} data-testid={`source-row-${idx}`} className="border border-slate-200 rounded-sm p-3 space-y-2 bg-slate-50/40">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[0.65rem] font-mono text-slate-400 w-6 text-right">{idx + 1}.</span>
                <Input
                  data-testid={`source-name-${idx}`}
                  value={s.name}
                  onChange={(e) => updateSource(idx, { name: e.target.value })}
                  placeholder="Canonical name (e.g. SEESA)"
                  className="h-9 rounded-sm bg-white flex-1 min-w-[180px]"
                />
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    data-testid={`source-up-${idx}`}
                    onClick={() => moveSource(idx, -1)}
                    disabled={idx === 0}
                    className="h-8 w-8 rounded-sm border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-30 inline-flex items-center justify-center"
                    title="Move up"
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    type="button"
                    data-testid={`source-down-${idx}`}
                    onClick={() => moveSource(idx, 1)}
                    disabled={idx === sources.length - 1}
                    className="h-8 w-8 rounded-sm border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-30 inline-flex items-center justify-center"
                    title="Move down"
                  >
                    <ArrowDown size={12} />
                  </button>
                </div>
                <Button
                  data-testid={`source-remove-${idx}`}
                  size="sm"
                  variant="ghost"
                  onClick={() => removeSource(idx)}
                  className="rounded-sm text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-8 px-2"
                >
                  <X size={14} />
                </Button>
              </div>

              <div className="pl-8">
                <Label className="text-[0.6rem] uppercase tracking-wider text-slate-500">Aliases (substrings; case-insensitive)</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                  {(s.aliases || []).map((alias, ai) => (
                    <span
                      key={ai}
                      data-testid={`alias-${idx}-${ai}`}
                      className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded-sm px-2 py-1 text-xs font-mono"
                    >
                      {alias}
                      <button
                        type="button"
                        onClick={() => removeAlias(idx, ai)}
                        className="text-slate-400 hover:text-rose-600"
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                  <div className="flex items-center gap-1">
                    <Input
                      data-testid={`alias-input-${idx}`}
                      value={newAliasIdx[idx] || ""}
                      onChange={(e) => setNewAliasIdx((m) => ({ ...m, [idx]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAlias(idx); } }}
                      placeholder="Add alias…"
                      className="h-7 rounded-sm bg-white w-40 text-xs"
                    />
                    <Button
                      data-testid={`alias-add-${idx}`}
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 rounded-sm"
                      onClick={() => addAlias(idx)}
                    >
                      <Plus size={12} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="flex items-center gap-3 sticky bottom-4 z-10">
        <Button
          data-testid="sources-save-btn"
          onClick={save}
          disabled={saving}
          className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2"
        >
          <FloppyDisk size={14} /> {saving ? "Saving…" : "Save sources"}
        </Button>
        <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
          <Lightning size={11} /> Saving auto-recanonicalizes every existing lead.
        </span>
      </div>
    </div>
  );
}
